import React, { useEffect, useState } from 'react';
import { BadgeCheckIcon, ArrowRightIcon, CreditCardIcon, MoreVerticalIcon, SendIcon, ClockIcon, AlertCircleIcon, XIcon, CheckIcon } from 'lucide-react';
const Invoices = () => {
  // State to track which context menu is open
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  // State to track success message
  const [successMessage, setSuccessMessage] = useState<{
    id: number;
    message: string;
  } | null>(null);
  // Function to handle inviting a customer to LucaPay
  const handleInviteToLucaPay = (invoiceId: number, customer: string) => {
    // Close the menu
    setOpenMenuId(null);
    // In a real app, this would make an API call to send the invitation
    console.log(`Inviting ${customer} to use LucaPay for invoice #${invoiceId}`);
    // Show success message
    setSuccessMessage({
      id: invoiceId,
      message: `Invitation sent to ${customer}`
    });
    // Clear message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };
  // Comprehensive list of all invoices
  const invoices = [{
    id: 1,
    invoiceNumber: 'INV-2023-001',
    customer: 'ABC Corporation',
    amount: '$1,245.00',
    issueDate: '2023-06-15',
    dueDate: '2023-07-15',
    status: 'Pending',
    // This invoice is in upcoming payments, being processed by LucaPay
    inLucaPay: true,
    paymentType: 'Scheduled'
  }, {
    id: 2,
    invoiceNumber: 'INV-2023-002',
    customer: 'XYZ Industries',
    amount: '$876.50',
    issueDate: '2023-06-18',
    dueDate: '2023-07-18',
    status: 'Pending',
    // This invoice is in upcoming payments, being processed by LucaPay
    inLucaPay: true,
    paymentType: 'Pending Approval'
  }, {
    id: 3,
    invoiceNumber: 'INV-2023-003',
    customer: 'Global Enterprises',
    amount: '$2,340.00',
    issueDate: '2023-06-22',
    dueDate: '2023-07-22',
    status: 'Pending',
    // This invoice is in upcoming payments, being processed by LucaPay
    inLucaPay: true,
    paymentType: 'Scheduled'
  }, {
    id: 4,
    invoiceNumber: 'INV-2023-004',
    customer: 'Tech Solutions Inc.',
    amount: '$945.00',
    issueDate: '2023-06-10',
    dueDate: '2023-07-10',
    status: 'Paid',
    // This invoice is in payment history, was processed by LucaPay
    inLucaPay: true,
    paymentType: 'Completed'
  }, {
    id: 5,
    invoiceNumber: 'INV-2023-005',
    customer: 'Innovate Ltd.',
    amount: '$1,876.50',
    issueDate: '2023-06-08',
    dueDate: '2023-07-08',
    status: 'Paid',
    // This invoice is in payment history, was processed by LucaPay
    inLucaPay: true,
    paymentType: 'Completed'
  }, {
    id: 6,
    invoiceNumber: 'INV-2023-006',
    customer: 'Acme Co.',
    amount: '$750.00',
    issueDate: '2023-06-05',
    dueDate: '2023-07-05',
    status: 'Paid',
    // This invoice is NOT being processed by LucaPay
    inLucaPay: false
  }, {
    id: 7,
    invoiceNumber: 'INV-2023-007',
    customer: 'Quantum Systems',
    amount: '$1,450.00',
    issueDate: '2023-06-25',
    dueDate: '2023-07-25',
    status: 'Pending',
    // This invoice is NOT being processed by LucaPay
    inLucaPay: false
  }, {
    id: 8,
    invoiceNumber: 'INV-2023-008',
    customer: 'Future Technologies',
    amount: '$2,100.00',
    issueDate: '2023-06-28',
    dueDate: '2023-07-28',
    status: 'Pending',
    // This invoice is NOT being processed by LucaPay
    inLucaPay: false
  }, {
    id: 9,
    invoiceNumber: 'INV-2023-009',
    customer: 'Omega Industries',
    amount: '$3,200.00',
    issueDate: '2023-06-01',
    dueDate: '2023-07-01',
    status: 'Overdue',
    // This invoice is NOT being processed by LucaPay
    inLucaPay: false
  }, {
    id: 10,
    invoiceNumber: 'INV-2023-010',
    customer: 'Delta Corp',
    amount: '$980.00',
    issueDate: '2023-06-12',
    dueDate: '2023-07-12',
    status: 'Pending',
    // This invoice is NOT being processed by LucaPay
    inLucaPay: false
  }];
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  return <div className="space-y-6" data-id="element-322">
      {/* LucaPay Information Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-323">
        <div className="flex items-start" data-id="element-324">
          <div className="flex-shrink-0" data-id="element-325">
            <BadgeCheckIcon className="h-5 w-5 text-blue-600" data-id="element-326" />
          </div>
          <div className="ml-3" data-id="element-327">
            <h3 className="text-sm font-medium text-blue-800" data-id="element-328">
              Processing by LucaPay
            </h3>
            <div className="mt-2 text-sm text-blue-700" data-id="element-329">
              <p data-id="element-330">
                Invoices highlighted in blue are being processed through
                LucaPay, our secure payment processing service. These invoices
                are either scheduled for payment or have already been paid.
              </p>
            </div>
            <div className="mt-4" data-id="element-331">
              <div className="flex items-center" data-id="element-332">
                <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-333">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: '50%'
                }} data-id="element-334"></div>
                </div>
                <span className="ml-2 text-sm text-blue-700" data-id="element-335">
                  5/10 invoices processed via LucaPay
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div data-id="element-336">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-337">Invoices</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-338">
          View and manage all your invoices
        </p>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-339">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-340">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-341">
            Invoice List
          </h3>
          <div className="flex items-center space-x-2" data-id="element-342">
            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-343">
              <option data-id="element-344">All Invoices</option>
              <option data-id="element-345">Pending</option>
              <option data-id="element-346">Paid</option>
              <option data-id="element-347">Overdue</option>
              <option data-id="element-348">LucaPay Processing</option>
            </select>
          </div>
        </div>
        <div className="border-t border-gray-200" data-id="element-349">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-350">
            <thead className="bg-gray-50" data-id="element-351">
              <tr data-id="element-352">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-353">
                  Invoice #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-354">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-355">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-356">
                  Issue Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-357">
                  Due Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-358">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-359">
                  LucaPay
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-360">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-361">
              {invoices.map(invoice => <tr key={invoice.id} className={invoice.inLucaPay ? 'bg-blue-50' : ''} data-id="element-362">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-363">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-364">
                    {invoice.customer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-365">
                    {invoice.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-366">
                    {new Date(invoice.issueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-367">
                    {new Date(invoice.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-368">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invoice.status === 'Paid' ? 'bg-green-100 text-green-800' : invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-369">
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-370">
                    {invoice.inLucaPay ? <div className="flex items-center" data-id="element-371">
                        <CreditCardIcon className="h-4 w-4 text-blue-600 mr-1" data-id="element-372" />
                        <span className="text-blue-600 font-medium" data-id="element-373">
                          {invoice.paymentType}
                        </span>
                      </div> : <span className="text-gray-400" data-id="element-374">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative" data-id="element-375">
                    {!invoice.inLucaPay && <div className="relative" onClick={e => e.stopPropagation()} data-id="element-376">
                        <button type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onClick={() => setOpenMenuId(openMenuId === invoice.id ? null : invoice.id)} data-id="element-377">
                          <MoreVerticalIcon className="h-5 w-5" data-id="element-378" />
                        </button>
                        {/* Success message */}
                        {successMessage && successMessage.id === invoice.id && <div className="absolute right-0 mt-1 w-64 bg-green-50 border border-green-200 rounded-md shadow-lg z-10 py-2 px-3" data-id="element-379">
                            <div className="flex justify-between items-center" data-id="element-380">
                              <div className="flex items-center" data-id="element-381">
                                <CheckIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-382" />
                                <p className="text-sm text-green-700" data-id="element-383">
                                  {successMessage.message}
                                </p>
                              </div>
                              <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-500" data-id="element-384">
                                <XIcon className="h-4 w-4" data-id="element-385" />
                              </button>
                            </div>
                          </div>}
                        {/* Context menu */}
                        {openMenuId === invoice.id && <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-386">
                            <div className="py-1" data-id="element-387">
                              <button type="button" className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700" onClick={() => handleInviteToLucaPay(invoice.id, invoice.customer)} data-id="element-388">
                                <SendIcon className="h-4 w-4 mr-3 text-blue-500" data-id="element-389" />
                                Invite to LucaPay
                              </button>
                              {invoice.status === 'Pending' && <button type="button" className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-390">
                                  <ClockIcon className="h-4 w-4 mr-3 text-gray-500" data-id="element-391" />
                                  Send Payment Reminder
                                </button>}
                              {invoice.status === 'Overdue' && <button type="button" className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-392">
                                  <AlertCircleIcon className="h-4 w-4 mr-3 text-red-500" data-id="element-393" />
                                  Escalate Overdue Payment
                                </button>}
                            </div>
                          </div>}
                      </div>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-394">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-395">
            <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-396">
              Previous
            </a>
            <a href="#" className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-397">
              Next
            </a>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-398">
            <div data-id="element-399">
              <p className="text-sm text-gray-700" data-id="element-400">
                Showing <span className="font-medium" data-id="element-401">1</span> to{' '}
                <span className="font-medium" data-id="element-402">10</span> of{' '}
                <span className="font-medium" data-id="element-403">20</span> results
              </p>
            </div>
            <div data-id="element-404">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-405">
                <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-406">
                  <span className="sr-only" data-id="element-407">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-408">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-409" />
                  </svg>
                </a>
                <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-410">
                  1
                </a>
                <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-blue-50 text-sm font-medium text-blue-600 hover:bg-blue-100" data-id="element-411">
                  2
                </a>
                <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-412">
                  3
                </a>
                <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-413">
                  <span className="sr-only" data-id="element-414">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-415">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" data-id="element-416" />
                  </svg>
                </a>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Invoices;